import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";
import { GoogleReviewButton } from "../components/SiteFeatures";
import { usePublicData } from "../lib/publicData";
import { portfolioAlt } from '../lib/portfolioAlt';

const UNIVERS = [
  {
    key: "celebrations",
    n: "01",
    titre: "Célébrations",
    sous: "Mariages, baptêmes, événements familiaux",
    img: "/images/generated/service_celebration.jpg",
  },
  {
    key: "maternite",
    n: "02",
    titre: "Maternité & Bébé",
    sous: "Grossesse, nouveau-né, famille",
    img: "/images/generated/service_grossesse.jpg",
  },
  {
    key: "portrait",
    n: "03",
    titre: "Portrait & Branding",
    sous: "Portraits personnels, branding professionnel",
    img: "/images/generated/service_portrait.jpg",
  },
  {
    key: "studio",
    n: "04",
    titre: "Book & Headshot studio",
    sous: "Sessions studio, books, headshots",
    img: "/images/generated/service_studio.jpg",
  },
];

export default function Home() {
  const initial = usePublicData();
  const [portfolio, setPortfolio] = React.useState((initial.portfolio || []).filter(p=>p.featured).slice(0,8));
  const [testimonials, setTestimonials] = React.useState(initial.testimonials || []);
  const [catalog,setCatalog] = React.useState(initial.services || []);
  React.useEffect(() => {
    api.get("/portfolio", { params: { featured: true } }).then((r) => setPortfolio(r.data.slice(0, 8))).catch(()=>{});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(()=>{});
    api.get('/services').then(r=>setCatalog(r.data)).catch(()=>{});
  }, []);
  const shownTestimonials = testimonials.filter(t => t.featured !== false).slice(0, 6);
  const average = testimonials.length ? (testimonials.reduce((sum,t) => sum + Number(t.note || 0),0) / testimonials.length).toFixed(1) : null;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden section-dark" data-testid="home-hero">
        <Grain />
        <div className="absolute inset-0 z-0">
          <img
            src="/images/generated/home_hero.jpg"
            alt="Studio Euclides"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/60 to-dark/20" />
          <div className="absolute -top-40 -left-40 w-[700px] h-[700px] glow-amber animate-glowPulse" style={{ "--gx": "30%", "--gy": "40%" }} />
        </div>

        <div className="relative z-10 max-w-[1480px] mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="grid lg:grid-cols-12 gap-8 items-end"
          >
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6">Studio portrait — Écublens, Suisse</div>
              <h1 className="h-display text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[9rem]">
                Studio<br />
                <span className="italic-accent">Euclides.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <p className="text-text-cream/70 font-light text-lg max-w-sm lg:ml-auto leading-relaxed">
                Portraits qui restent. Une approche éditoriale du portrait, faite de patience,
                de matière et de lumière travaillée.
              </p>
              <p data-testid="home-starting-prices" className="text-[#D6A86C] text-sm mt-4 leading-relaxed">Portrait dès CHF 350 · Maternité dès CHF 350 · Mariages dès CHF 1'590</p>
              <div className="flex flex-wrap gap-4 mt-8 lg:justify-end">
                <Link to="/reserver-seance" className="btn-primary" data-testid="cta-book-session">Réserver ma séance <ArrowRight size={14}/></Link>
                <a href="#univers" className="inline-flex items-center gap-2 text-sm border-b border-white/30 py-2" data-testid="cta-discover">
                  Découvrir mon univers <ArrowRight size={14} strokeWidth={1.5} />
                </a>
                <Link to="/atelier" className="btn-secondary" data-testid="cta-atelier">
                  L'Atelier Lumière
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 eyebrow text-text-cream/40 z-10">
          Scroll
        </div>
      </section>

      {/* 4 UNIVERS */}
      <section id="univers" className="section-cream py-24 md:py-32 px-6 md:px-12" data-testid="univers-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="eyebrow-dark mb-4">Les 4 univers</div>
              <h2 className="font-serif text-5xl md:text-6xl font-light leading-[0.95]">
                Une photographie<br /><span className="italic text-accent-deep">pour chaque chapitre</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-text-dark/70 font-light text-lg leading-relaxed">
                De l'attente d'une naissance à la fierté d'une marque personnelle, chaque univers
                a son langage et ses gestes. Choisis le tien.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {UNIVERS.map((u, i) => (
              <article
                key={u.key}
                data-testid={`univers-card-${u.key}`}
                className={`group relative aspect-[4/5] md:aspect-[5/4] overflow-hidden hover-zoom ${
                  i === 0 ? "md:row-span-2 md:aspect-[3/4]" : ""
                }`}
              >
                <img src={u.img} alt={`${u.titre} — photographie Studio Euclides à Écublens`} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/85 via-dark/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="eyebrow text-accent-light mb-2">{u.n}</div>
                  <h3 className="font-serif text-3xl md:text-4xl text-text-cream">{u.titre}</h3>
                  <p className="text-text-cream/90 font-light mt-2 text-sm">{u.sous}</p>
                  <p data-testid={`universe-price-${u.key}`} className="mt-3 text-[#D6A86C]">Dès CHF {({celebrations: "1'590", maternite: "350", portrait: "350", studio: "190"})[u.key]}</p>
                  <div className="mt-4 flex gap-5 flex-wrap items-center">
                    <Link to={`/reserver-seance?univers=${u.key}`} data-testid={`universe-book-${u.key}`} className="btn-primary">Réserver <ArrowRight size={14}/></Link>
                    <Link to={`/univers/${u.key}`} data-testid={`universe-discover-${u.key}`} className="text-sm text-white border-b border-white/60">Découvrir cet univers</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO TEASER */}
      <section className="section-dark py-24 md:py-32 px-6 md:px-12 relative overflow-hidden" data-testid="portfolio-teaser">
        <div className="absolute -top-40 right-0 w-[500px] h-[500px] glow-amber-soft" />
        <div className="max-w-[1480px] mx-auto relative z-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="eyebrow mb-4">Portfolio</div>
              <h2 className="font-serif text-5xl md:text-6xl font-light">Un aperçu<br/><span className="italic-accent">en images.</span></h2>
            </div>
            <Link to="/portfolio" className="btn-secondary" data-testid="cta-portfolio">
              Voir tout le portfolio <ArrowRight size={14} strokeWidth={1.5}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {portfolio.map((img, i) => (
              <Link to="/portfolio" key={img.id} className={`relative aspect-[3/4] overflow-hidden hover-zoom ${i === 0 || i === 5 ? "md:row-span-2 md:aspect-auto" : ""}`}>
                <img src={img.url} alt={portfolioAlt(img)} loading="lazy" className="w-full h-full object-cover" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ATELIER TEASER */}
      <section className="section-dark py-32 md:py-48 px-6 md:px-12 relative overflow-hidden" data-testid="atelier-teaser">
        <div className="absolute inset-0 glow-amber animate-glowPulse" style={{ "--gx": "70%", "--gy": "50%" }} />
        <Grain />
        <div className="max-w-[1280px] mx-auto relative z-10 text-center">
          <div className="eyebrow mb-6">Workshop · Niveau 01</div>
          <h2 className="font-serif text-6xl md:text-8xl lg:text-9xl font-light leading-[0.9]">
            L'Atelier<br/><span className="italic-accent">Lumière.</span>
          </h2>
          <p className="text-text-cream/70 font-light text-lg max-w-xl mx-auto mt-8 leading-relaxed">
            4 heures pour comprendre la lumière studio. 3 setups, 4 participants, un modèle pro.
            Pas de blabla. Tu poses, tu shootes, tu apprends.
          </p>
          <div className="mt-12">
            <Link to="/atelier" className="btn-primary" data-testid="cta-atelier-main">
              Réserver ma place <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* LOCATION STUDIO */}
      <section id="location-studio" className="section-cream py-24 md:py-32 px-6 md:px-12" data-testid="location-studio-section">
        <div className="max-w-[1480px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 lg:order-1">
            <div className="eyebrow-dark mb-6">Louer le plateau</div>
            <h2 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] text-text-dark">
              Studio photo<br/><span className="italic text-accent-deep">en location.</span>
            </h2>
            <p className="text-text-dark/70 font-light text-lg mt-8 leading-relaxed max-w-lg">
              45 m² équipés Godox à Écublens. Plateau prêt à tourner, en autonomie
              ou avec assistance photographe en option.
            </p>

            <ul className="mt-10 space-y-3 text-text-dark/80 font-light">
              <li className="flex gap-3" data-testid="loc-feat-flashs">
                <span>8 flashs Godox · AD200 · AD100 · V1 · MS300 · DP400 · SK300 · SK400</span>
              </li>
              <li className="flex gap-3" data-testid="loc-feat-modif">
                <span>8 modificateurs · Octa 120 · Beauty Dish · Paraboliques · Strip Box</span>
              </li>
              <li className="flex gap-3" data-testid="loc-feat-fonds">
                <span>Fond vert + fonds neutres · idéal keying &amp; packshot</span>
              </li>
              <li className="flex gap-3" data-testid="loc-feat-cap">
                <span>Jusqu'à 6 personnes · vestiaire &amp; coin maquillage</span>
              </li>
            </ul>

            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <div className="border-t border-text-dark/20 pt-4" data-testid="loc-price-demi">
                <div className="eyebrow-dark text-xs">Demi-journée</div>
                <div className="font-serif text-4xl mt-2 text-text-dark">CHF {catalog.find(s=>s.slug==='location-demi-journee')?.price_chf ?? '—'}</div>
                <div className="text-text-dark/50 text-sm font-light mt-1">4 heures</div>
              </div>
              <div className="border-t border-accent-deep pt-4" data-testid="loc-price-jour">
                <div className="eyebrow-dark text-xs text-accent-deep">Journée</div>
                <div className="font-serif text-4xl mt-2 text-text-dark">CHF {catalog.find(s=>s.slug==='location-journee')?.price_chf ?? '—'}</div>
                <div className="text-text-dark/50 text-sm font-light mt-1">8 heures</div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/reservation"
                className="btn-dark"
                data-testid="cta-location-reserver"
              >
                Réserver le studio <ArrowRight size={14} strokeWidth={1.5}/>
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-text-dark/30 text-text-dark/80 font-light uppercase tracking-[0.2em] text-xs hover:border-text-dark hover:text-text-dark transition" data-testid="cta-location-infos">
                Plus d'infos
              </Link>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src="/images/generated/service_studio.jpg"
                alt="Studio Euclides en location — plateau Godox 45m²"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-text-dark text-text-cream py-3 px-6">
              <div className="eyebrow text-xs">Écublens · Suisse</div>
              <div className="font-serif italic text-accent-light text-2xl mt-1">45 m²</div>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="section-cream py-24 md:py-32 px-6 md:px-12" data-testid="testimonials-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="text-center mb-16">
            <div className="eyebrow-dark mb-4">Ils racontent</div>
            <h2 className="font-serif text-5xl md:text-6xl font-light">Ce qu'on en <span className="italic text-accent-deep">dit.</span></h2>
            <div className="mt-6 flex items-center justify-center gap-2 text-text-dark/60">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-accent-deep stroke-accent-deep"/>)}
              </div>
              <span className="eyebrow-dark text-xs" data-testid="home-review-count">{average && `${average}/5 · `}{shownTestimonials.length} avis sur {testimonials.length}</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {shownTestimonials.map((t) => (
              <div key={t.id} className="border-t border-text-dark/15 pt-6" data-testid={`testimonial-${t.id}`}>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.note }).map((_, i) => (
                    <Star key={i} size={12} className="fill-accent-deep stroke-accent-deep" />
                  ))}
                </div>
                <p className="font-serif text-xl md:text-2xl italic font-light leading-snug text-text-dark">
                  « {t.texte} »
                </p>
                <div className="mt-6 flex items-center justify-between">
                  <div className="eyebrow-dark">
                    {t.prenom} {t.nom_initial || ""}
                  </div>
                  {t.service && <div className="text-text-dark/40 text-xs font-light">{t.service}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <div className="mb-6"><GoogleReviewButton id="home"/></div>
            <Link
              to="/avis"
              className="inline-flex items-center gap-2 text-text-dark hover:text-accent-deep transition text-sm tracking-wide border-b border-text-dark/30 hover:border-accent-deep pb-1"
              data-testid="home-all-reviews-link"
            >
              Lire tous les {testimonials.length} avis <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Contact */}
      <section className="section-dark py-24 md:py-32 px-6 md:px-12 text-center" data-testid="home-contact-cta">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-5xl md:text-6xl font-light">Une <span className="italic-accent">idée</span> en tête ?</h2>
          <p className="text-text-cream/70 font-light text-lg mt-6">
            Parlons de ton projet. Une séance, un événement, une marque. La meilleure image est
            toujours celle qu'on prépare ensemble.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cta-contact">Me contacter</Link>
            <Link to="/services" className="btn-secondary" data-testid="cta-services">Voir tous mes services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
